// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Cms1500Form, Cms1500ProviderRole } from './cms1500-form';
import { CMS1500_LETTERS, CMS1500_MAX_LINES, CMS1500_STATES, cms1500Number, cms1500TotalCharge } from './cms1500-form';

/** One problem that blocks submission, keyed by the form field path (`ins.dob`, `lines.0.cpt`). */
export interface Cms1500Issue {
  /** Dot path of the offending field in {@link Cms1500Form}. */
  key: string;
  /** CMS-1500 box the field lives in (`1a`, `24F`, `Payer`, `Sub`). */
  box: string;
  message: string;
}

const PO_BOX = /\b(p\.?\s*o\.?\s*box|post\s+office\s+box)\b/i;
const NAME_CHARS = /^[A-Za-z0-9 .,'&\-/]*$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const ICD10 = /^[A-TV-Z][0-9][0-9A-Z](\.?[0-9A-Z]{1,4})?$/i;
const TAXONOMY = /^[0-9A-Z]{9}X$/i;
const PROVIDER_LABEL: Record<Cms1500ProviderRole, string> = {
  '82': 'Rendering provider',
  DN: 'Referring provider',
  DK: 'Ordering provider',
  DQ: 'Supervising provider',
};

const blank = (value: unknown): boolean => value == null || String(value).trim() === '';
const digits = (value: string | undefined): string => String(value ?? '').replace(/\D/g, '');

/** Luhn check-digit test for a 10-digit NPI (prefix 80840). */
export function isValidNpi(value: string | undefined): boolean {
  const npi = String(value ?? '').trim();
  if (!/^\d{10}$/.test(npi)) {
    return false;
  }
  const full = `80840${npi}`;
  let sum = 0;
  for (let i = 0; i < full.length; i++) {
    let n = Number(full[full.length - 1 - i]);
    if (i % 2 === 1) {
      n *= 2;
      if (n > 9) {
        n -= 9;
      }
    }
    sum += n;
  }
  return sum % 10 === 0;
}

function get(form: Cms1500Form, key: string): unknown {
  let current: unknown = form;
  for (const part of key.split('.')) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Validate a CMS-1500 form against Stedi's 1500 Professional Claim required fields (the red-asterisk
 * fields on Stedi's form) plus the 5010 837P situational rules Stedi enforces on submit — subscriber
 * demographics, billing ZIP+4, no PO Box, NPI check digit, balanced charges.
 * @param form - The claim form.
 * @param today - ISO date used for "can't be in the future" checks (defaults to now).
 * @returns Every issue that blocks submission; empty when the claim is ready.
 */
export function validateCms1500Form(form: Cms1500Form, today = new Date().toISOString().slice(0, 10)): Cms1500Issue[] {
  const issues: Cms1500Issue[] = [];
  const add = (key: string, box: string, message: string): void => {
    issues.push({ key, box, message });
  };
  const str = (key: string): string => String(get(form, key) ?? '');

  const name = (prefix: string, box: string, who: string, requireLast: boolean, requireFirst: boolean): void => {
    if (requireLast && blank(str(`${prefix}.last`))) {
      add(`${prefix}.last`, box, `${who} last name is required`);
    } else if (str(`${prefix}.last`).length > 60) {
      add(`${prefix}.last`, box, 'Last name can be at most 60 characters');
    }
    if (requireFirst && blank(str(`${prefix}.first`))) {
      add(`${prefix}.first`, box, `${who} first name is required`);
    }
    for (const part of ['last', 'first', 'middle', 'suffix']) {
      const value = str(`${prefix}.${part}`);
      if (value && !NAME_CHARS.test(value)) {
        add(`${prefix}.${part}`, box, "Use letters, numbers, spaces, and . , ' - & / only");
      }
    }
  };
  const address = (prefix: string, box: string, who: string, required: boolean, strict = false): void => {
    const any = ['line1', 'line2', 'city', 'state', 'zip'].some((part) => !blank(str(`${prefix}.${part}`)));
    if (!required && !any) {
      return;
    }
    if (blank(str(`${prefix}.line1`))) {
      add(`${prefix}.line1`, box, `${who} address line 1 is required`);
    } else if (strict && PO_BOX.test(`${str(`${prefix}.line1`)} ${str(`${prefix}.line2`)}`)) {
      add(`${prefix}.line1`, box, `${who} address can't be a PO Box — use the physical location`);
    }
    if (blank(str(`${prefix}.city`))) {
      add(`${prefix}.city`, box, 'City is required');
    }
    if (blank(str(`${prefix}.state`))) {
      add(`${prefix}.state`, box, 'State is required');
    } else if (!CMS1500_STATES.includes(str(`${prefix}.state`).trim().toUpperCase())) {
      add(`${prefix}.state`, box, 'Select a state from the list');
    }
    const zip = str(`${prefix}.zip`).trim();
    if (!zip) {
      add(`${prefix}.zip`, box, 'ZIP Code is required');
    } else if (!/^\d{5}(-?\d{4})?$/.test(zip)) {
      add(`${prefix}.zip`, box, 'Enter a 5- or 9-digit ZIP Code');
    } else if (strict && digits(zip).length !== 9) {
      add(`${prefix}.zip`, box, `${who} ZIP Code needs the 4-digit extension (ZIP+4)`);
    }
  };
  const notFuture = (key: string, box: string, label: string): void => {
    const value = str(key);
    if (value && value > today) {
      add(key, box, `${label} can't be in the future`);
    }
  };
  const range = (from: string, to: string, box: string, label: string): void => {
    const start = str(from);
    const end = str(to);
    if (start && !end) {
      add(to, box, `Enter the end date for ${label}`);
    } else if (end && !start) {
      add(from, box, `Enter the start date for ${label}`);
    } else if (start && end && end < start) {
      add(to, box, 'End date must be on or after the start date');
    }
  };
  const npi = (key: string, box: string, who: string, required: boolean): void => {
    const value = str(key).trim();
    if (!value) {
      if (required) {
        add(key, box, `${who} NPI is required`);
      }
      return;
    }
    if (!/^\d{10}$/.test(value)) {
      add(key, box, 'NPI must be exactly 10 digits');
    } else if (!isValidNpi(value)) {
      add(key, box, 'NPI fails the check-digit test — verify the number');
    }
  };
  const phone = (key: string, box: string): void => {
    const value = str(key);
    if (!blank(value) && digits(value).length !== 10) {
      add(key, box, 'Phone numbers are 10 digits');
    }
  };
  const taxonomy = (key: string, box: string): void => {
    const value = str(key).trim();
    if (value && !TAXONOMY.test(value)) {
      add(key, box, 'Taxonomy codes are 10 characters ending in X, like 207RS0012X');
    }
  };

  // Transmission — only electronic, chargeable claims go through Stedi's claims API
  if (form.send !== 'electronic') {
    add('send', 'Send', 'Mailed claims aren’t supported — Polaris sends claims electronically through Stedi');
  }
  if (form.claimType !== 'chargeable') {
    add(
      'claimType',
      'ID',
      'Stedi’s claims API only accepts chargeable claims — send reporting or subrogation claims from the Stedi portal'
    );
  }

  // Payer and box 1
  if (blank(form.payer.id)) {
    add('payer.id', 'Payer', 'Select the payer to send this claim to');
  }
  if (blank(form.insType)) {
    add('insType', '1', 'Select an insurance type');
  }
  if (blank(form.respLevel)) {
    add('respLevel', '1a', 'Select a payment responsibility level');
  }
  if (blank(form.memberId)) {
    add('memberId', '1a', 'Insured ID number is required');
  } else if (!/^[A-Za-z0-9-]{2,80}$/.test(form.memberId.trim())) {
    add('memberId', '1a', 'Use 2–80 letters, numbers, or hyphens — no spaces');
  }

  // Boxes 2–7, 11a — patient and insured
  const self = form.rel === 'self';
  name('ins', '4', 'Insured', true, true);
  if (self) {
    name('pat', '2', 'Patient', false, false);
    address('pat', '5', 'Patient', false);
    if (blank(form.ins.dob)) {
      add('ins.dob', '11a', 'Insured date of birth is required when the patient is the insured');
    }
    if (blank(form.ins.sex)) {
      add('ins.sex', '11a', "Select the insured's sex");
    }
  } else {
    name('pat', '2', 'Patient', true, true);
    if (blank(form.pat.dob)) {
      add('pat.dob', '3', "Patient date of birth is required when the patient isn't the insured");
    }
    if (blank(form.pat.sex)) {
      add('pat.sex', '3', "Select the patient's sex");
    }
    address('pat', '5', 'Patient', true);
  }
  notFuture('pat.dob', '3', 'Date of birth');
  notFuture('ins.dob', '11a', 'Date of birth');
  if (blank(form.rel)) {
    add('rel', '6', "Select the patient's relationship to the insured");
  }
  address('ins', '7', 'Insured', self);

  // Box 9 — other insured
  if (form.other) {
    name('other', '9', 'Other insured', true, true);
    if (blank(form.other.policy)) {
      add('other.policy', '9a', "Other insured's policy or group number is required");
    }
    if (blank(form.other.memberId)) {
      add('other.memberId', '9', "Other insured's member ID is required");
    }
    if (blank(form.other.plan)) {
      add('other.plan', '9d', 'Other insurance plan name is required');
    }
    if (blank(form.other.payerId)) {
      add('other.payerId', '9d', "Other payer's ID is required");
    }
    if (blank(form.other.respLevel)) {
      add('other.respLevel', '9', "Select the other payer's responsibility level");
    } else if (form.other.respLevel === form.respLevel) {
      add('other.respLevel', '9', 'The other payer needs a different responsibility level than this payer');
    }
  }

  // Box 10 — related causes (the 837P carries at most two)
  if (form.cond.auto === 'yes' && blank(form.cond.autoState)) {
    add('cond.autoState', '10b', 'Select the state where the auto accident happened');
  }
  if ([form.cond.emp, form.cond.auto, form.cond.other].filter((answer) => answer === 'yes').length > 2) {
    add('cond.other', '10', 'Answer Yes to at most two of employment, auto accident, and other');
  }
  if (form.cond.codes.length > 4) {
    add('cond.codes', '10d', 'Add up to 4 claim codes');
  }

  // Box 11b
  if (!blank(form.ins.ocid) && blank(form.ins.ocq)) {
    add('ins.ocq', '11b', 'Select a qualifier for the other claim ID');
  }
  if (!blank(form.ins.ocq) && blank(form.ins.ocid)) {
    add('ins.ocid', '11b', 'Enter the other claim ID');
  }

  // Boxes 12, 13
  if (blank(form.sig12)) {
    add('sig12', '12', "Select an option for the patient's signature");
  }
  if (blank(form.sig13)) {
    add('sig13', '13', "Select an option for the insured's signature");
  }

  // Boxes 14–16, 18 — dates
  if (form.dates['431'] && form.dates['484']) {
    add('dates.484', '14', 'Enter only one date in box 14 — onset or LMP');
  }
  for (const qualifier of Object.keys(form.dates)) {
    notFuture(`dates.${qualifier}`, qualifier === '431' || qualifier === '484' ? '14' : '15', 'This date');
  }
  range('unableFrom', 'unableTo', '16', 'dates unable to work');
  range('hospFrom', 'hospTo', '18', 'hospitalization dates');

  // Box 17 — providers: last name and NPI required once any field is filled
  for (const role of Object.keys(PROVIDER_LABEL) as Cms1500ProviderRole[]) {
    const provider = form.prov[role];
    const filled = ['last', 'first', 'middle', 'suffix', 'npi', 'lic', 'g2', 'upin', 'taxonomy'].some(
      (part) => !blank(provider[part as keyof typeof provider])
    );
    if (!filled) {
      continue;
    }
    name(`prov.${role}`, '17', PROVIDER_LABEL[role], true, false);
    npi(`prov.${role}.npi`, '17b', PROVIDER_LABEL[role], true);
    taxonomy(`prov.${role}.taxonomy`, '17');
  }

  // Box 19
  if (!blank(form.n19) && blank(form.n19q)) {
    add('n19q', '19a', 'Select a qualifier for the note');
  }
  if (form.n19.length > 80) {
    add('n19', '19a', 'Notes can be at most 80 characters');
  }
  form.att.forEach((attachment, index) => {
    const prefix = `att.${index}`;
    if (blank(attachment.type)) {
      add(`${prefix}.type`, '19b', 'Select a report type');
    }
    if (blank(attachment.trans)) {
      add(`${prefix}.trans`, '19b', 'Select how the attachment is sent');
    }
    if (attachment.trans && attachment.trans !== 'AA' && blank(attachment.ctrl)) {
      add(`${prefix}.ctrl`, '19b', "Control number is required unless it's available on request (AA)");
    }
  });

  // Boxes 22, 23
  const replacing = form.resubQ === '7' || form.resubQ === '8';
  if (replacing && blank(form.origRef)) {
    add('origRef', '22', 'Original reference number is required for a replacement or void');
  }
  if (!blank(form.origRef) && !replacing) {
    add('resubQ', '22', 'Choose 7 (replacement) or 8 (void) to send an original reference number');
  }
  if (!blank(form.clia) && !/^\d{2}D\d{7}$/i.test(form.clia.trim())) {
    add('clia', '23', 'CLIA numbers are 10 characters, like 05D0123456');
  }

  // Box 21 — diagnoses A–L, no gaps, valid and unique
  const dx = form.dx.map((code) =>
    String(code ?? '')
      .trim()
      .toUpperCase()
  );
  if (!dx[0]) {
    add('dx.0', '21', 'Enter the primary diagnosis (A)');
  }
  const gap = dx.findIndex((code) => !code);
  if (gap > 0) {
    const next = dx.findIndex((code, index) => index > gap && code);
    if (next > 0) {
      add(
        `dx.${gap}`,
        '21',
        `Fill diagnosis ${CMS1500_LETTERS[gap]} before ${CMS1500_LETTERS[next]} — codes can't skip letters`
      );
    }
  }
  dx.forEach((code, index) => {
    if (!code) {
      return;
    }
    if (!ICD10.test(code)) {
      add(`dx.${index}`, '21', `${code} isn't a valid ICD-10-CM code`);
    } else if (dx.indexOf(code) !== index) {
      add(`dx.${index}`, '21', `${code} is already entered as ${CMS1500_LETTERS[dx.indexOf(code)]}`);
    }
  });

  // Box 24 — service lines
  if (!form.lines.length) {
    add('lines', '24', 'Add at least one service line');
  }
  if (form.lines.length > CMS1500_MAX_LINES) {
    add('lines', '24', `A claim can have at most ${CMS1500_MAX_LINES} service lines`);
  }
  const dob = self ? form.ins.dob : form.pat.dob;
  form.lines.forEach((line, index) => {
    const prefix = `lines.${index}.`;
    const label = `Line ${index + 1}: `;
    if (blank(line.from)) {
      add(`${prefix}from`, '24A', `${label}date of service is required`);
    } else {
      if (line.from > today) {
        add(`${prefix}from`, '24A', `${label}date of service can't be in the future`);
      }
      if (dob && line.from < dob) {
        add(`${prefix}from`, '24A', `${label}date of service is before the patient's date of birth`);
      }
    }
    if (line.to && line.from && line.to < line.from) {
      add(`${prefix}to`, '24A', `${label}end date must be on or after the start date`);
    }
    if (blank(line.pos)) {
      add(`${prefix}pos`, '24B', `${label}place of service is required`);
    }
    if (blank(line.cpt)) {
      add(`${prefix}cpt`, '24D', `${label}procedure code is required`);
    } else if (!/^[A-Za-z0-9]{5}$/.test(line.cpt.trim())) {
      add(`${prefix}cpt`, '24D', `${label}procedure codes are 5 characters (CPT or HCPCS)`);
    }
    line.mods.forEach((mod, modIndex) => {
      if (!blank(mod) && !/^[A-Za-z0-9]{2}$/.test(mod.trim())) {
        add(`${prefix}mods.${modIndex}`, '24D', `${label}modifiers are 2 characters`);
      }
    });
    if (!line.ptrs.length) {
      add(`${prefix}ptrs`, '24E', `${label}point to at least one diagnosis code`);
    } else {
      for (const letter of line.ptrs) {
        if (!dx[CMS1500_LETTERS.indexOf(letter)]) {
          add(`${prefix}ptrs`, '24E', `${label}pointer ${letter} has no diagnosis in box 21`);
        }
      }
    }
    const charge = cms1500Number(line.charge);
    if (blank(line.charge)) {
      add(`${prefix}charge`, '24F', `${label}charge amount is required`);
    } else if (charge == null || charge <= 0) {
      add(`${prefix}charge`, '24F', `${label}charge must be greater than $0.00`);
    }
    const units = cms1500Number(line.units);
    if (blank(line.units)) {
      add(`${prefix}units`, '24G', `${label}units are required`);
    } else if (units == null || units <= 0) {
      add(`${prefix}units`, '24G', `${label}units must be greater than 0`);
    }
    if (!blank(line.renderNpi)) {
      npi(`${prefix}renderNpi`, '24J', 'Rendering provider', false);
    }
    if (!blank(line.ndc)) {
      if (digits(line.ndc).length !== 11) {
        add(`${prefix}ndc`, '24', `${label}NDC must be 11 digits (5-4-2)`);
      }
      if (blank(line.ndcQty)) {
        add(`${prefix}ndcQty`, '24', `${label}NDC quantity is required with an NDC`);
      }
      if (blank(line.ndcUnit)) {
        add(`${prefix}ndcUnit`, '24', `${label}NDC unit is required with an NDC`);
      }
    }
    if (!blank(line.labCharge)) {
      const lab = cms1500Number(line.labCharge);
      if (lab == null || lab < 0) {
        add(`${prefix}labCharge`, '20', `${label}enter a dollar amount for the outside lab charge`);
      }
      if (blank(line.labProviderId)) {
        add(`${prefix}labProviderId`, '20', `${label}outside lab identifier is required with a lab charge`);
      }
    }
    if (line.note.length > 80) {
      add(`${prefix}note`, '24', `${label}line notes can be at most 80 characters`);
    }
  });

  // Boxes 25–31
  const taxId = form.taxId.trim();
  if (!taxId) {
    add('taxId', '25', 'Federal tax ID number is required');
  } else if (form.taxType === 'EIN' && !/^\d{2}-?\d{7}$/.test(taxId)) {
    add('taxId', '25', 'EINs are 9 digits, like 12-3456789');
  } else if (form.taxType === 'SSN' && !/^\d{3}-?\d{2}-?\d{4}$/.test(taxId)) {
    add('taxId', '25', 'SSNs are 9 digits, like 123-45-6789');
  }
  if (blank(form.acct)) {
    add('acct', '26', 'Patient account number is required');
  } else if (!/^[A-Za-z0-9]{1,17}$/.test(form.acct.trim())) {
    add('acct', '26', 'Patient account numbers are up to 17 letters or numbers');
  }
  if (blank(form.assign)) {
    add('assign', '27', 'Select whether you accept assignment');
  }
  if (!blank(form.amtPaid)) {
    const paid = cms1500Number(form.amtPaid);
    if (paid == null || paid < 0) {
      add('amtPaid', '29', 'Enter a dollar amount');
    } else if (paid > cms1500TotalCharge(form)) {
      add('amtPaid', '29', "Amount paid can't be more than the total charge");
    }
  }
  if (blank(form.sig31)) {
    add('sig31', '31', 'Select an option for the physician or supplier signature');
  }

  // Submitter
  if (form.sub.type === 'ind') {
    name('sub', 'Sub', 'Submitter', true, true);
  } else if (blank(form.sub.org)) {
    add('sub.org', 'Sub', 'Submitter organization name is required');
  }
  if (blank(form.sub.phone) && blank(form.sub.email) && blank(form.sub.fax)) {
    add('sub.phone', 'Sub', 'Add a phone number, fax, or email for the submitter contact');
  }
  phone('sub.phone', 'Sub');
  phone('sub.fax', 'Sub');
  if (!blank(form.sub.email) && !EMAIL.test(form.sub.email.trim())) {
    add('sub.email', 'Sub', 'Enter a valid email address');
  }

  // Box 32 — service facility, only when any field is filled
  if (['org', 'line1', 'line2', 'city', 'state', 'zip', 'npi'].some((part) => !blank(str(`fac.${part}`)))) {
    if (blank(form.fac.org)) {
      add('fac.org', '32', 'Service facility name is required');
    }
    address('fac', '32', 'Service facility', true, true);
    npi('fac.npi', '32a', 'Service facility', false);
  }

  // Box 33 — billing provider
  if (form.bill.type === 'ind') {
    name('bill', '33', 'Billing provider', true, true);
  } else if (blank(form.bill.org)) {
    add('bill.org', '33', 'Billing provider organization name is required');
  }
  address('bill', '33', 'Billing provider', true, true);
  phone('bill.phone', '33');
  npi('bill.npi', '33a', 'Billing provider', true);
  taxonomy('bill.taxonomy', '33b');

  return issues;
}
